import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TopTabs } from "@/components/PageHeader";
import { ListToolbar, FilterBox, DataTable } from "@/components/ListTools";
import { useSelection, BulkBar } from "@/components/BulkSelect";
import { FileText, Upload, Pencil, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATES = ["DE", "PA", "NJ", "MD", "NY", "VA", "DC", "Other"];

export default function CourtForms() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const canEdit = user?.role !== "client";
  const isAdmin = user?.role === "admin";
  const load = () => api.get("/court-forms").then((r) => setList(r.data));
  const [builtin, setBuiltin] = useState([]);
  const loadBuiltin = () => api.get("/court-forms/builtin").then((r) => setBuiltin(r.data)).catch(() => {});
  useEffect(() => { load(); loadBuiltin(); }, []);

  const filtered = useMemo(() => list.filter((f) => {
    if (state !== "all" && f.state !== state) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return [f.name, f.form_number, f.court_name, f.county, f.description, f.state].some((v) => v?.toLowerCase().includes(t));
  }), [list, state, q]);
  const stateOptions = useMemo(() => [...new Set([...STATES, ...list.map((f) => f.state)])].filter(Boolean), [list]);
  const selection = useSelection(filtered.map((f) => f.id));
  const fileUrl = (f) => `${api.defaults.baseURL}${(f.file?.url || "").replace(/^\/api/, "")}`;

  const columns = [
    { key: "name", label: "Form", render: (f) => (
      <div>
        <button type="button" className="sm-link font-semibold text-left" onClick={() => setEditing(f)} data-testid={`form-link-${f.id}`}>{f.name}</button>
        {f.field_count === 0 && <span className="ml-2 inline-flex items-center rounded bg-amber-100 text-amber-900 text-[10px] px-1.5 py-0.5 font-semibold" title="This PDF has no form fields, so it can't be pre-filled" data-testid={`form-not-fillable-${f.id}`}>Not fillable</span>}
        {f.field_count > 0 && <span className="ml-2 inline-flex items-center rounded bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 font-semibold" data-testid={`form-fillable-${f.id}`}>{f.field_count} fields</span>}
        {f.description && <div className="text-xs text-slate-500 mt-0.5">{f.description}</div>}
      </div>
    ) },
    { key: "number", label: "Form #", render: (f) => f.form_number || "—" },
    { key: "court", label: "Court", render: (f) => <span className="uppercase">{[f.court_name, f.county].filter(Boolean).join(" · ") || "—"}</span> },
    { key: "state", label: "State", render: (f) => f.state },
    { key: "file", label: "Document", render: (f) => f.file ? (
      <a href={fileUrl(f)} target="_blank" rel="noreferrer" className="sm-link inline-flex items-center gap-1" data-testid={`form-file-${f.id}`}><FileText className="h-3.5 w-3.5 text-rose-600" />{f.file.filename}</a>
    ) : <span className="text-xs text-slate-400">No file</span> },
    ...(canEdit ? [{ key: "actions", label: "", className: "text-right whitespace-nowrap", render: (f) => (
      <div className="flex justify-end gap-2 text-slate-400">
        <button type="button" title="Edit" className="hover:text-slate-900" data-testid={`edit-form-${f.id}`} onClick={() => setEditing(f)}><Pencil className="h-4 w-4" /></button>
        {isAdmin && <button type="button" title="Delete" className="hover:text-rose-600" data-testid={`delete-form-${f.id}`} onClick={async () => { if (!window.confirm("Delete this form?")) return; await api.delete(`/court-forms/${f.id}`); toast.success("Deleted"); load(); }}><Trash2 className="h-4 w-4" /></button>}
      </div>
    ) }] : []),
  ];

  return (
    <div data-testid="court-forms-page">
      <TopTabs tabs={[{ label: "Court Forms" }]} value="Court Forms" />
      <div className="px-4 sm:px-7 py-6 space-y-6">
        <ListToolbar title="Court Forms" search={q} onSearch={setQ} placeholder="Form, number, court, county"
          filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((o) => !o)} onClear={() => { setQ(""); setState("all"); }}
          action={canEdit ? <Button className="sm-green" data-testid="new-form-button" onClick={() => setEditing({})}>Add Form</Button> : null} />
        {filtersOpen && (
          <div className="flex gap-6 flex-wrap">
            <FilterBox title="State">
              <select value={state} onChange={(e) => setState(e.target.value)} className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm" data-testid="state-filter-select">
                <option value="all">All states</option>{stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FilterBox>
          </div>
        )}
        <div className="text-sm text-slate-600" data-testid="forms-count">{filtered.length} of {list.length} Forms</div>
        {canEdit && builtin.filter((b) => !b.installed).length > 0 && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex flex-wrap items-center gap-3" data-testid="builtin-forms-banner">
            <div className="text-sm text-emerald-900 flex-1 min-w-[240px]"><b>Ready-made fillable forms</b> — pre-mapped so every field auto-fills from the job and attempts.</div>
            {builtin.filter((b) => !b.installed).map((b) => (
              <Button key={b.key} size="sm" className="sm-green" data-testid={`install-builtin-${b.key}`} onClick={async () => { try { await api.post(`/court-forms/builtin/${b.key}`); toast.success(`${b.name} added to your library`); load(); loadBuiltin(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } }}>
                <Plus className="h-3.5 w-3.5 mr-1" />{b.state} · {b.name}
              </Button>
            ))}
          </div>
        )}
        {isAdmin && <BulkBar selection={selection} noun="form" actions={[{ label: "Delete", icon: Trash2, className: "text-rose-600 border-rose-200 hover:bg-rose-50", confirm: "Permanently delete {n} form(s)?", onClick: async (ids) => { await api.post("/court-forms/bulk", { ids, action: "delete" }); toast.success(`${ids.length} form(s) deleted`); load(); } }]} />}
        <DataTable columns={columns} rows={filtered} rowKey={(f) => f.id} emptyText="No forms in the library yet." testId="court-forms-table" selection={isAdmin ? selection : null} />
      </div>
      {editing !== null && <FormDialog form={editing.id ? editing : null} readOnly={!canEdit} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FormDialog({ form, readOnly, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", state: "DE", form_number: "", court_name: "", county: "", description: "", ...(form || {}) });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name) return toast.error("Form name required");
    setSaving(true);
    try {
      const body = { name: f.name, state: f.state, form_number: f.form_number, court_name: f.court_name, county: f.county, description: f.description };
      const { data } = form ? await api.patch(`/court-forms/${form.id}`, body) : await api.post("/court-forms", body);
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/court-forms/${data.id}/file`, fd);
      }
      toast.success(form ? "Form updated" : "Form added"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="form-dialog">
        <DialogHeader><DialogTitle>{form ? form.name : "Add court form"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Row label="Form name *"><Input data-testid="form-name" disabled={readOnly} value={f.name} onChange={set("name")} placeholder="Affidavit of Service" /></Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="State">
              <select data-testid="form-state" disabled={readOnly} value={f.state} onChange={set("state")} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                {[...new Set([...STATES, f.state])].filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Row>
            <Row label="Form number"><Input data-testid="form-number" disabled={readOnly} value={f.form_number || ""} onChange={set("form_number")} /></Row>
          </div>
          <Row label="Court"><Input data-testid="form-court" disabled={readOnly} value={f.court_name || ""} onChange={set("court_name")} placeholder="Superior Court of Delaware" /></Row>
          <Row label="County / division"><Input data-testid="form-county" disabled={readOnly} value={f.county || ""} onChange={set("county")} placeholder="New Castle County" /></Row>
          <Row label="Notes"><Textarea disabled={readOnly} rows={2} value={f.description || ""} onChange={set("description")} /></Row>
          {!readOnly && (
            <Row label="Blank document (PDF or image)">
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="form-file-input" />
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="form-file-button"><Upload className="h-4 w-4 mr-2" />{form?.file ? "Replace file" : "Choose file"}</Button>
                <span className="text-xs text-slate-500 truncate">{file ? file.name : form?.file ? `Current: ${form.file.filename}` : "No file selected"}</span>
              </div>
            </Row>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{readOnly ? "Close" : "Cancel"}</Button>
            {!readOnly && <Button type="submit" className="sm-green" disabled={saving} data-testid="form-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form ? "Save" : "Add form"}</Button>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
function Row({ label, children }) {
  return <div className="space-y-1"><Label className="sm-label">{label}</Label>{children}</div>;
}
